import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import {
  fetchSeminars, fetchSeminar, fetchSeminarQuestionnaire, generateSeminar, deleteSeminar,
  synthesizeSpeech
} from '../services/api';
import {
  ArrowLeft, Play, Pause, Search, Trash2, Video, Sparkles,
  ArrowRight, CheckCircle2, Loader2, Clock, ChevronRight, X,
  MessageCircle, Users, Leaf, CalendarDays, LayoutGrid, Volume2, BookOpen
} from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import TalkingAvatar from '../components/TalkingAvatar';
import VideoLibraryTab from '../components/VideoLibraryTab';

const VOICES = [
  { id: 'nova', label: 'Nova (energetic)' },
  { id: 'alloy', label: 'Alloy (neutral)' },
  { id: 'shimmer', label: 'Shimmer (bright)' },
  { id: 'echo', label: 'Echo (calm)' },
  { id: 'fable', label: 'Fable (storyteller)' },
  { id: 'onyx', label: 'Onyx (authoritative)' },
];

const CATEGORIES = ['', 'nutrition', 'exercise', 'sleep', 'mental_wellness', 'longevity', 'supplements', 'lifestyle', 'general'];
const CAT_LABELS = { nutrition: 'Nutrition', exercise: 'Exercise', sleep: 'Sleep', mental_wellness: 'Mental Wellness', longevity: 'Longevity', supplements: 'Supplements', lifestyle: 'Lifestyle', general: 'General' };
const CAT_COLORS = { nutrition: '#66BB6A', exercise: '#EF5350', sleep: '#5C6BC0', mental_wellness: '#FF9800', longevity: '#6B8E23', supplements: '#8D6E63', lifestyle: '#42A5F5', general: '#78909C' };

export default function SeminarsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState('list'); // list | questionnaire | generating | player
  const [listMode, setListMode] = useState('grid'); // grid | calendar | library
  const [seminars, setSeminars] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [allSeminars, setAllSeminars] = useState([]);

  // Questionnaire
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [qStep, setQStep] = useState(0);
  const [qLoading, setQLoading] = useState(false);
  const [prefCat, setPrefCat] = useState('');

  // Generation
  const [genStage, setGenStage] = useState('');

  // Player
  const [activeSeminar, setActiveSeminar] = useState(null);
  const [segIndex, setSegIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [ttsLoading, setTtsLoading] = useState(false);
  const [voice, setVoice] = useState('nova');
  const avatarRef = useRef(null);
  const typingTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const abortSpeakRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchSeminars(catFilter, search);
      setSeminars(data);
      if (!catFilter && !search) setAllSeminars(data);
    } catch {}
    setLoading(false);
  }, [catFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Stop all playback helper
  const stopAllPlayback = useCallback(() => {
    abortSpeakRef.current = true;
    if (typingTimerRef.current) { clearInterval(typingTimerRef.current); typingTimerRef.current = null; }
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (avatarRef.current) {
      try { avatarRef.current.stop(); } catch {}
    }
  }, []);

  // Speak segment via OpenAI TTS + 3D avatar lip-sync.
  // A synchronized typewriter runs alongside for captions.
  useEffect(() => {
    if (!playing || !activeSeminar) return;
    const seg = activeSeminar.segments[segIndex];
    if (!seg) { setPlaying(false); return; }
    const fullText = seg.type === 'qa' ? seg.answer : seg.text;
    if (!fullText || !fullText.trim()) { setPlaying(false); return; }

    abortSpeakRef.current = false;
    let cancelled = false;
    setDisplayText('');
    setTtsLoading(true);

    (async () => {
      let tts;
      try {
        tts = await synthesizeSpeech(fullText, voice, 'tts-1');
      } catch (e) {
        console.error('TTS failed', e);
        if (!cancelled) {
          setTtsLoading(false);
          // Fallback: typewriter only so the user still sees something
          setDisplayText(fullText);
          advanceTimerRef.current = setTimeout(() => {
            if (segIndex < activeSeminar.segments.length - 1) setSegIndex(i => i + 1);
            else setPlaying(false);
          }, 2000);
        }
        return;
      }
      if (cancelled || abortSpeakRef.current) return;
      setTtsLoading(false);

      // Kick the caption typewriter proportional to audio length
      const approxMs = tts.approx_duration_ms || Math.max(1000, fullText.length * 65);
      const stepMs = Math.max(12, Math.min(45, approxMs / Math.max(1, fullText.length)));
      let charIdx = 0;
      typingTimerRef.current = setInterval(() => {
        charIdx++;
        setDisplayText(fullText.slice(0, charIdx));
        if (charIdx >= fullText.length) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }, stepMs);

      // Drive the 3D avatar
      try {
        if (avatarRef.current) {
          await avatarRef.current.speak({
            text: fullText,
            audio_base64: tts.audio_base64,
            words: tts.words,
            wtimes: tts.wtimes,
            wdurations: tts.wdurations,
            visemes: tts.visemes,
            vtimes: tts.vtimes,
            vdurations: tts.vdurations,
          });
        }
      } catch (e) {
        console.error('Avatar speak failed', e);
      }

      if (cancelled || abortSpeakRef.current) return;

      // Ensure caption is fully shown when audio ends
      setDisplayText(fullText);
      if (typingTimerRef.current) { clearInterval(typingTimerRef.current); typingTimerRef.current = null; }

      advanceTimerRef.current = setTimeout(() => {
        if (cancelled || abortSpeakRef.current) return;
        if (segIndex < activeSeminar.segments.length - 1) setSegIndex(i => i + 1);
        else setPlaying(false);
      }, 1500);
    })();

    return () => {
      cancelled = true;
      stopAllPlayback();
      setTtsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, segIndex, activeSeminar, voice]);

  const startQuestionnaire = async () => {
    setQLoading(true);
    setView('questionnaire');
    try {
      const data = await fetchSeminarQuestionnaire();
      setQuestions(data.questions);
      setAnswers(new Array(data.questions.length).fill(''));
      setQStep(0);
    } catch { setQuestions(['What health topic interests you?', 'Beginner or advanced?', 'Current health challenge?', 'Prefer tips or science?', 'Health goal for next 3 months?']); setAnswers(new Array(5).fill('')); }
    setQLoading(false);
  };

  const handleGenerate = async () => {
    setView('generating');
    setGenStage('analyzing');
    setTimeout(() => setGenStage('clustering'), 2000);
    setTimeout(() => setGenStage('scripting'), 5000);
    try {
      const result = await generateSeminar({ questionnaire_answers: answers.filter(a => a.trim()), preferred_category: prefCat });
      setActiveSeminar(result);
      setGenStage('done');
      load();
    } catch {
      alert('Failed to generate seminar. Try again.');
      setView('list');
    }
  };

  const openSeminar = async (id) => {
    try {
      const data = await fetchSeminar(id);
      setActiveSeminar(data);
      setSegIndex(0);
      setPlaying(false);
      setDisplayText('');
      setView('player');
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this seminar?')) return;
    try { await deleteSeminar(id); load(); } catch {}
  };

  // Legacy CSS avatar removed — replaced by <TalkingAvatar /> in the player view.

  // Player view
  if (view === 'player' && activeSeminar) {
    const seg = activeSeminar.segments[segIndex];
    const progress = activeSeminar.segments.length > 0 ? ((segIndex + 1) / activeSeminar.segments.length) * 100 : 0;
    const catColor = CAT_COLORS[activeSeminar.category] || COLORS.primary;

    return (
      <div data-testid="seminar-player" style={{ maxWidth: 800, margin: '0 auto', padding: '24px 20px' }}>
        <button data-testid="back-from-player" onClick={() => { setView('list'); setPlaying(false); stopAllPlayback(); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600 }}><ArrowLeft size={18} /> Back to Seminars</button>

        {/* Video-like container */}
        <div style={{ backgroundColor: '#1a1a2e', borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
          {/* Stage — 3D talking avatar */}
          <div style={{ padding: '20px 24px 16px' }}>
            <TalkingAvatar ref={avatarRef} height={340} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>ThriveLonger.ai Host</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{activeSeminar.title}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Volume2 size={14} color="rgba(255,255,255,0.6)" />
                <select
                  data-testid="voice-select"
                  value={voice}
                  onChange={e => {
                    const newVoice = e.target.value;
                    if (playing) {
                      // Restart current segment with the new voice
                      stopAllPlayback();
                      setPlaying(false);
                      setVoice(newVoice);
                      // Re-trigger playback on next tick
                      setTimeout(() => setPlaying(true), 50);
                    } else {
                      setVoice(newVoice);
                    }
                  }}
                  style={{ background: 'rgba(255,255,255,0.08)', color: '#FFF', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 8px', fontSize: 12 }}>
                  {VOICES.map(v => <option key={v.id} value={v.id} style={{ color: '#000' }}>{v.label}</option>)}
                </select>
              </div>
            </div>

            {/* Current segment caption */}
            <div style={{ marginTop: 18 }}>
              {seg?.type === 'qa' && (
                <div style={{ backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 16px', marginBottom: 12, borderLeft: `3px solid ${catColor}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Users size={14} color="rgba(255,255,255,0.5)" />
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>AUDIENCE QUESTION</span>
                  </div>
                  <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)', margin: 0, fontStyle: 'italic' }}>{seg.question}</p>
                </div>
              )}
              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: '14px 18px', minHeight: 60 }}>
                {seg?.type === 'qa' && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}><Leaf size={14} color={COLORS.primary} /><span style={{ fontSize: 11, color: COLORS.primary, fontWeight: 600 }}>AVATAR RESPONSE</span></div>}
                <p data-testid="seminar-caption" style={{ fontSize: 15, color: '#FFF', lineHeight: 1.6, margin: 0, minHeight: 20 }}>
                  {ttsLoading && !displayText ? (
                    <span style={{ opacity: 0.7, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Loader2 size={14} className="spin" /> Synthesizing voice…
                    </span>
                  ) : (
                    <>
                      {playing ? displayText : (seg?.type === 'qa' ? seg?.answer : seg?.text) || ''}
                      {playing && <span style={{ opacity: 0.5, animation: 'blink 1s infinite' }}>|</span>}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ padding: '12px 24px 16px', backgroundColor: 'rgba(0,0,0,0.3)' }}>
            <div style={{ height: 4, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ height: 4, width: `${progress}%`, backgroundColor: COLORS.primary, borderRadius: 2, transition: 'width 0.5s' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <button onClick={() => { if (segIndex > 0) { stopAllPlayback(); setSegIndex(i => i - 1); setPlaying(false); } }} disabled={segIndex === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, opacity: segIndex === 0 ? 0.3 : 1 }}>
                <ArrowLeft size={20} color="#FFF" />
              </button>
              <button data-testid="play-btn" onClick={() => { if (playing) { stopAllPlayback(); setPlaying(false); } else { setPlaying(true); } }} style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: COLORS.primary, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {playing ? <Pause size={22} color="#FFF" /> : <Play size={22} color="#FFF" style={{ marginLeft: 2 }} />}
              </button>
              <button onClick={() => { if (segIndex < activeSeminar.segments.length - 1) { stopAllPlayback(); setSegIndex(i => i + 1); setPlaying(false); } }} disabled={segIndex >= activeSeminar.segments.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, opacity: segIndex >= activeSeminar.segments.length - 1 ? 0.3 : 1 }}>
                <ArrowRight size={20} color="#FFF" />
              </button>
            </div>
            <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>Segment {segIndex + 1} of {activeSeminar.segments.length}</div>
          </div>
        </div>

        {/* Segment list */}
        <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Seminar Segments</h3>
          {activeSeminar.segments.map((s, i) => (
            <button key={i} onClick={() => { stopAllPlayback(); setSegIndex(i); setPlaying(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%', backgroundColor: i === segIndex ? `${COLORS.primary}10` : 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left', marginBottom: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: i === segIndex ? COLORS.primary : COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: i === segIndex ? '#FFF' : COLORS.textSecondary }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: i === segIndex ? COLORS.primary : COLORS.textPrimary }}>
                  {s.type === 'intro' ? 'Introduction' : s.type === 'closing' ? 'Closing' : s.question?.slice(0, 60) + (s.question?.length > 60 ? '...' : '')}
                </div>
              </div>
              {s.type === 'qa' && <MessageCircle size={14} color={COLORS.textSecondary} />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Generating view
  if (view === 'generating') {
    const stages = { analyzing: 'Analyzing your profile & answers...', clustering: 'Running cluster analysis on health data...', scripting: 'Generating seminar script with AI...', done: 'Seminar ready!' };
    return (
      <div data-testid="seminar-generating" style={{ maxWidth: 500, margin: '80px auto', padding: '0 20px', textAlign: 'center' }}>
        {genStage === 'done' && activeSeminar ? (
          <>
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: `${COLORS.success}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><CheckCircle2 size={36} color={COLORS.success} /></div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Seminar Created!</h2>
            <p style={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary, marginTop: 8 }}>{activeSeminar.title}</p>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 4 }}>{activeSeminar.segments?.length} segments · {activeSeminar.duration}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
              <button data-testid="watch-seminar-btn" onClick={() => { setSegIndex(0); setView('player'); }} style={{ padding: '14px 28px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}><Play size={18} /> Watch Now</button>
              <button onClick={() => setView('list')} style={{ padding: '14px 28px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, cursor: 'pointer' }}>Later</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Sparkles size={32} color={COLORS.primary} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{stages[genStage] || 'Generating...'}</h2>
            <div style={{ width: 200, height: 4, backgroundColor: COLORS.surfaceSecondary, borderRadius: 2, margin: '24px auto', overflow: 'hidden' }}>
              <div style={{ height: 4, backgroundColor: COLORS.primary, borderRadius: 2, width: genStage === 'analyzing' ? '30%' : genStage === 'clustering' ? '60%' : '90%', transition: 'width 1s ease' }} />
            </div>
          </>
        )}
      </div>
    );
  }

  // Questionnaire view
  if (view === 'questionnaire') {
    if (qLoading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textSecondary }}>Preparing your questions...</div>;
    const q = questions[qStep];
    return (
      <div data-testid="seminar-questionnaire" style={{ maxWidth: 560, margin: '40px auto', padding: '0 20px' }}>
        <button onClick={() => setView('list')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600 }}><ArrowLeft size={18} /> Back</button>
        <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>{questions.map((_, i) => <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: i <= qStep ? COLORS.primary : COLORS.border }} />)}</div>
        <div style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 28 }}>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 6 }}>Question {qStep + 1} of {questions.length}</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 16 }}>{q}</h2>
          <textarea data-testid="q-answer" value={answers[qStep] || ''} onChange={e => { const a = [...answers]; a[qStep] = e.target.value; setAnswers(a); }} placeholder="Type your answer..." rows={3} style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 15, color: COLORS.textPrimary, resize: 'vertical', fontFamily: 'inherit' }} />
          {qStep === questions.length - 1 && (
            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, display: 'block', marginBottom: 4 }}>Preferred category (optional)</label>
              <select value={prefCat} onChange={e => setPrefCat(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14, color: COLORS.textPrimary }}>
                <option value="">Auto-detect from answers</option>
                {CATEGORIES.filter(c => c).map(c => <option key={c} value={c}>{CAT_LABELS[c] || c}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            {qStep > 0 && <button onClick={() => setQStep(s => s - 1)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, cursor: 'pointer' }}>Back</button>}
            {qStep < questions.length - 1 ? (
              <button data-testid="q-next" onClick={() => setQStep(s => s + 1)} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>Next <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="generate-seminar-btn" onClick={handleGenerate} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.success, color: '#FFF', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Sparkles size={16} /> Generate Seminar</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Helper: get seminars for a specific date
  const getSeminarsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const source = catFilter ? seminars : allSeminars;
    return source.filter(s => s.created_at?.startsWith(dateStr));
  };

  // Dates that have seminars (for calendar highlighting)
  const seminarDates = (catFilter ? seminars : allSeminars).reduce((acc, s) => {
    if (s.created_at) {
      const d = s.created_at.split('T')[0];
      acc[d] = (acc[d] || 0) + 1;
    }
    return acc;
  }, {});

  const selectedDateSeminars = selectedDate ? getSeminarsForDate(selectedDate) : [];

  // List view
  return (
    <div data-testid="seminars-page" style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
          <Video size={26} color={COLORS.primary} />
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Seminars</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* View toggle */}
          <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
            <button data-testid="view-grid" onClick={() => setListMode('grid')}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, backgroundColor: listMode === 'grid' ? COLORS.primary : COLORS.surface, color: listMode === 'grid' ? '#FFF' : COLORS.textSecondary }}>
              <LayoutGrid size={14} /> Grid
            </button>
            <button data-testid="view-calendar" onClick={() => { setListMode('calendar'); setSelectedDate(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, backgroundColor: listMode === 'calendar' ? COLORS.primary : COLORS.surface, color: listMode === 'calendar' ? '#FFF' : COLORS.textSecondary }}>
              <CalendarDays size={14} /> Calendar
            </button>
            <button data-testid="view-library" onClick={() => setListMode('library')}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, backgroundColor: listMode === 'library' ? COLORS.primary : COLORS.surface, color: listMode === 'library' ? '#FFF' : COLORS.textSecondary }}>
              <BookOpen size={14} /> Library
            </button>
          </div>
          <button data-testid="create-seminar-btn" onClick={startQuestionnaire} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Sparkles size={14} /> New Seminar
          </button>
        </div>
      </div>

      {/* Category filters (hidden in library mode — it has its own filter) */}
      {listMode !== 'library' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {CATEGORIES.map(c => (
            <button key={c || 'all'} onClick={() => setCatFilter(c)} style={{ padding: '6px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', backgroundColor: catFilter === c ? COLORS.primary : COLORS.surface, color: catFilter === c ? '#FFF' : COLORS.textSecondary }}>
              {c ? (CAT_LABELS[c] || c) : 'All'}
            </button>
          ))}
        </div>
      )}

      {/* Library View */}
      {listMode === 'library' ? (
        <VideoLibraryTab />
      ) : listMode === 'calendar' ? (
        <div data-testid="seminar-calendar-view">
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {/* Calendar */}
            <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', flex: '0 0 auto' }}>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => setSelectedDate(date)}
                modifiers={{
                  hasSeminar: Object.keys(seminarDates).map(d => new Date(d + 'T12:00:00')),
                }}
                modifiersStyles={{
                  hasSeminar: { backgroundColor: `${COLORS.primary}20`, color: COLORS.primary, fontWeight: 700, borderRadius: '50%' },
                }}
              />
              <div style={{ padding: '8px 4px 0', fontSize: 12, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: `${COLORS.primary}30` }} />
                Dates with seminars
              </div>
            </div>

            {/* Selected date seminars */}
            <div style={{ flex: 1, minWidth: 280 }}>
              {selectedDate ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 12 }}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                  {selectedDateSeminars.length === 0 ? (
                    <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 32, textAlign: 'center' }}>
                      <CalendarDays size={32} color={COLORS.border} />
                      <p style={{ color: COLORS.textSecondary, marginTop: 10, fontSize: 14 }}>No seminars on this date</p>
                      <button onClick={startQuestionnaire} style={{ marginTop: 12, padding: '10px 20px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Create One</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {selectedDateSeminars.map(s => {
                        const catColor = CAT_COLORS[s.category] || COLORS.primary;
                        return (
                          <button key={s.seminar_id} data-testid={`cal-seminar-${s.seminar_id}`} onClick={() => openSeminar(s.seminar_id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 14, backgroundColor: COLORS.surface, borderRadius: 14, padding: 16, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                            <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Play size={20} color="#FFF" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, backgroundColor: `${catColor}15`, color: catColor, textTransform: 'capitalize' }}>{CAT_LABELS[s.category] || s.category}</span>
                                <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{s.duration}</span>
                              </div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{s.title}</div>
                              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{s.summary?.slice(0, 80)}{s.summary?.length > 80 ? '...' : ''}</div>
                            </div>
                            <ChevronRight size={16} color={COLORS.textSecondary} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 40, textAlign: 'center' }}>
                  <CalendarDays size={40} color={COLORS.border} />
                  <p style={{ color: COLORS.textSecondary, marginTop: 12, fontSize: 15 }}>Select a date to view seminars</p>
                  <p style={{ color: COLORS.textSecondary, fontSize: 13, marginTop: 4 }}>Highlighted dates have published seminars</p>
                </div>
              )}

              {/* Upcoming seminars summary */}
              {!selectedDate && allSeminars.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Recent Seminars</div>
                  {(catFilter ? seminars : allSeminars).slice(0, 5).map(s => {
                    const catColor = CAT_COLORS[s.category] || COLORS.primary;
                    return (
                      <button key={s.seminar_id} onClick={() => openSeminar(s.seminar_id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%', backgroundColor: 'transparent', border: 'none', borderBottom: `1px solid ${COLORS.border}20`, cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ width: 4, height: 32, borderRadius: 2, backgroundColor: catColor }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>{s.title}</div>
                          <div style={{ fontSize: 12, color: COLORS.textSecondary }}>{new Date(s.created_at).toLocaleDateString()} · {s.duration}</div>
                        </div>
                        <Play size={14} color={COLORS.textSecondary} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Search (grid view only) */}
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <Search size={18} color={COLORS.textSecondary} style={{ position: 'absolute', left: 14, top: 12 }} />
            <input data-testid="seminar-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search seminars by keyword..." style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 14, color: COLORS.textPrimary, backgroundColor: COLORS.surface }} />
          </div>
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>Loading...</div> : seminars.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Video size={56} color={COLORS.border} />
          <h3 style={{ color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>No Seminars Yet</h3>
          <p style={{ color: COLORS.textSecondary, marginTop: 8 }}>Create your first AI-generated seminar tailored to your health goals.</p>
          <button onClick={startQuestionnaire} style={{ marginTop: 16, padding: '14px 28px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer' }}>Create Your First Seminar</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {seminars.map(s => {
            const catColor = CAT_COLORS[s.category] || COLORS.primary;
            return (
              <div key={s.seminar_id} data-testid={`seminar-${s.seminar_id}`} style={{ backgroundColor: COLORS.surface, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                {/* Thumbnail */}
                <div onClick={() => openSeminar(s.seminar_id)} style={{ backgroundColor: '#1a1a2e', padding: '28px 20px', textAlign: 'center', position: 'relative' }}>
                  <div style={{ width: 56, height: 56, borderRadius: 28, background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}><Leaf size={28} color="#FFF" /></div>
                  <div style={{ position: 'absolute', bottom: 8, right: 12, padding: '3px 8px', borderRadius: 6, backgroundColor: 'rgba(0,0,0,0.6)', fontSize: 11, color: '#FFF', fontWeight: 600 }}>{s.duration}</div>
                  <div style={{ position: 'absolute', top: 8, left: 12, width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={16} color="#FFF" /></div>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, backgroundColor: `${catColor}15`, color: catColor, textTransform: 'capitalize' }}>{CAT_LABELS[s.category] || s.category}</span>
                    <span style={{ fontSize: 11, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={12} /> {s.duration}</span>
                  </div>
                  <h3 onClick={() => openSeminar(s.seminar_id)} style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 4, cursor: 'pointer' }}>{s.title}</h3>
                  <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.4, marginBottom: 10 }}>{s.summary?.slice(0, 100)}{s.summary?.length > 100 ? '...' : ''}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{new Date(s.created_at).toLocaleDateString()}</span>
                    <button onClick={e => { e.stopPropagation(); handleDelete(s.seminar_id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Trash2 size={14} color={COLORS.textSecondary} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}
